import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { formatDate } from '../../shared/tenantClock';
import { useShipmentWeightUnit } from '../../shared/useShipmentWeightUnit';
import { carriageLabel } from './ShipmentCarriageFields';
import ShipmentPackages from './ShipmentPackages';
import ShipmentEditDrawer from './ShipmentEditDrawer';
import { shipmentShippingCharge } from './packageShared';
import ShipmentReverseDialog from './ShipmentReverseDialog';

const smallButton = { padding: '6px 12px', borderRadius: 6, border: '1px solid #1976d2', background: '#eaf2fb', color: '#1976d2', fontSize: 12, fontWeight: 700, cursor: 'pointer' };
const dangerButton = { ...smallButton, border: '1px solid #b91c1c', background: '#fff', color: '#b91c1c' };
const quietButton = { ...smallButton, border: '1px solid #bfc8d6', background: '#fff', color: '#333' };

// Pack and Ship's Shipped section: the order's shipments so far, each with
// its date, carrier, tracking and packages with what's in them. Folded until
// opened — it's history, the open work sits above it. Edit corrects a
// shipment's details; Reverse un-ships it, putting its packages back as
// closed packages (in Packages, above) to reopen and change or ship again.
// Reversed shipments stay on Lists › Shipments only. `refreshKey` changes
// whenever the order's shipped quantities do, so a Ship pass shows here;
// onReversed reloads the order. Packing List reprints a shipment's (issues
// #296, #445), for anyone who can see it.
function ShippedPanel({ order, refreshKey, canUpdate, onLabel, onSenderLabels, onPackingList, onReversed }) {
  const weightUnit = useShipmentWeightUnit();
  const [shipments, setShipments] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reversing, setReversing] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/shipments', { params: { order_type: order.order_type, order_id: order.id, sortBy: 'shipped_date', sortOrder: 'ASC', limit: 100 } })
      .then((res) => { if (!cancelled) setShipments((res.data.data || []).filter((s) => !s.reversed_at)); })
      .catch(() => { if (!cancelled) setShipments([]); });
    return () => { cancelled = true; };
  }, [order.order_type, order.id, refreshKey, reloadTick]);

  if (!shipments.length) return null;

  return (
    <div style={{ marginTop: 16 }}>
      {/* Headed like Packages and SKUs above it. */}
      <div style={{ fontSize: 12, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Shipments
      </div>
      <div style={{ background: '#fff', border: '1px solid #e3e7ee', borderRadius: 8 }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '10px 12px', fontSize: 13, fontWeight: 600, color: '#333', cursor: 'pointer' }}
        >
          {open ? '▾' : '▸'} {shipments.length} {shipments.length === 1 ? 'shipment' : 'shipments'}
        </button>
        {open && shipments.map((s) => (
          <div key={s.id} style={{ borderTop: '1px solid #eceef1', padding: '10px 12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 6 }}>
              <span style={{ fontWeight: 700, fontSize: 14 }}>{[s.display_number, s.shipped_date ? formatDate(s.shipped_date) : 'No date'].filter(Boolean).join(' · ')}</span>
              <span style={{ fontSize: 12, color: '#666', textAlign: 'right' }}>{carriageLabel(s) || 'No carrier'}</span>
            </div>
            <div style={{ fontSize: 12, color: '#666', marginBottom: 6 }}>
              Tracking # {s.tracking_number || '—'}
              {/* Its Invoice (issue #269), opened from Lists › Shipments. */}
              {s.invoice && ` · Invoice ${s.invoice.display_number}${s.invoice.voided_at ? ' (Void)' : ''}`}
            </div>
            <ShipmentPackages
              packages={s.packages}
              weightUnit={weightUnit}
              onLabel={onLabel}
              onSenderLabels={onSenderLabels ? () => onSenderLabels(s.id) : null}
              shippingCharge={shipmentShippingCharge(s)}
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
              <button type="button" style={quietButton} onClick={() => onPackingList(s.id)}>Packing List</button>
              {canUpdate && (
                <>
                  <button type="button" style={smallButton} onClick={() => setEditing(s)}>Edit</button>
                  <button type="button" style={dangerButton} onClick={() => setReversing(s)}>Reverse</button>
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ShipmentEditDrawer
          shipment={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); setReloadTick((t) => t + 1); }}
        />
      )}
      {reversing && (
        <ShipmentReverseDialog
          shipment={reversing}
          onClose={() => setReversing(null)}
          onReversed={() => { setReversing(null); setReloadTick((t) => t + 1); onReversed(); }}
        />
      )}
    </div>
  );
}

export default ShippedPanel;
